import { randomId } from 'pano-utils';
import SparkMD5 from 'spark-md5';
import {
  type PanoCheckResult,
  type PanoCollectionItem,
  type PanoSuffix,
  PanoTaskType,
} from '@types';
import { sendWarning } from '@components/Noti';
import panoImageCheck from './panoImageCheck';

interface TaskFile {
  md5: string;
  file: File;
  suffix: PanoSuffix;
}
type OnComplete = (item: PanoCollectionItem) => void;
type CollectionMap = Map<PanoSuffix, PanoCheckResult>;

const kCollection = Symbol('kCollection');
const kOnComplete = Symbol('kOnComplete');
const kFormatSinglePano = Symbol('kFormatSinglePano');
const kFormatGroupPano = Symbol('kFormatGroupPano');

const getGroupData = (map: CollectionMap) => {
  const keys: PanoSuffix[] = ['f', 'b', 'l', 'r', 'u', 'd'];
  const md5Arr: string[] = [];
  const files: TaskFile[] = [];
  let size = 0;
  for (let i = 0; i < keys.length; i++) {
    const suffix = keys[i];
    const x = map.get(suffix)!;
    md5Arr.push(x.md5);
    files.push({ md5: x.md5, suffix: x.suffix, file: x.file });
    size += x.size;
  }
  const md5 = SparkMD5.hash(md5Arr.join(''));
  return { md5, size, files };
};

export default class PanoCollection {
  [kCollection]: Map<string, CollectionMap>;

  [kOnComplete]: OnComplete;

  constructor(onCompleteOne: OnComplete) {
    this[kCollection] = new Map();
    this[kOnComplete] = onCompleteOne;
  }

  /**
   * 添加图片文件
   */
  addFile = async (file: File) => {
    try {
      const result = await panoImageCheck(file);
      if (result.type === PanoTaskType.single) this[kFormatSinglePano](result);
      if (result.type === PanoTaskType.group) this[kFormatGroupPano](result);
    } catch (e) {
      sendWarning(e.message);
    }
  };

  /**
   * 检查不完整的六面体图片
   */
  uncompleteCheck = () => {
    if (this[kCollection].size) {
      const names = [...this[kCollection].keys()].join(', ');
      sendWarning(`${names} 六面体图片数量不足`);
    }
  };

  [kFormatSinglePano] = (result: PanoCheckResult) => {
    const taskFile: TaskFile = {
      file: result.file,
      md5: result.md5,
      suffix: result.suffix,
    };
    this[kOnComplete]({
      exif: result.exif,
      files: [taskFile],
      gps: result.gps,
      height: result.height,
      id: result.id,
      name: result.name,
      size: result.size,
      src: result.src,
      type: result.type,
      width: result.width,
    });
  };

  [kFormatGroupPano] = (result: PanoCheckResult) => {
    const { name, suffix } = result;
    let collectionMap = this[kCollection].get(name);
    if (!collectionMap) {
      collectionMap = new Map();
      this[kCollection].set(name, collectionMap);
    }
    collectionMap.set(suffix, result);
    if (collectionMap.size === 6) {
      const { md5, size, files } = getGroupData(collectionMap);
      const id = randomId(`${md5}_`);
      const front = collectionMap.get('f')!;
      this[kOnComplete]({
        exif: front.exif,
        files,
        gps: front.gps,
        height: front.height,
        id,
        name: front.name.replace(/[_.]$/, ''),
        size,
        src: front.src,
        type: PanoTaskType.group,
        width: front.width,
      });
      this[kCollection].delete(name);
    }
  };
}
